import { useCallback, useEffect, useRef, useState } from 'react';
import { LocationLookup } from './LocationLookup';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PosterRecognitionResult {
  title: string;
  artist?: string;
  year: string;
  type: string;
  dimensions: string;
  condition: string;
  rawContent: string;
  citations: string[];
  searchResults: Array<{
    title: string;
    url: string;
    date: string | null;
  }>;
  inventoryMatches?: Array<{
    _id: string;
    sku: string;
    title?: string;
    artist?: string;
    images: string[];
    createdAt: string;
    count?: number;
  }>;
}

interface PosterValuationResult {
  priceRange: {
    min: number;
    max: number;
    currency: string;
  };
  justification: string;
  marketData: {
    recent: {
      listings: Array<{
        price: number;
        date: string;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
    current: {
      listings: Array<{
        price: number;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
  };
  rawContent: string;
  citations: string[];
  searchResults: Array<{
    title: string;
    url: string;
    date: string | null;
  }>;
}

// Helper function to detect mobile devices
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

interface PosterProps {
  credentials: { username: string; password: string };
  dbMode: 'demo' | 'live';
  onAuthError?: () => void;
}

export function Poster({
  credentials,
  dbMode: _dbMode,
  onAuthError,
}: PosterProps) {
  const dbMode = _dbMode;
  const [posterResult, setPosterResult] =
    useState<PosterRecognitionResult | null>(null);
  const [posterError, setPosterError] = useState('');
  const [posterLoading, setPosterLoading] = useState(false);
  const [posterImages, setPosterImages] = useState<File[]>([]);
  const [posterImagePreviews, setPosterImagePreviews] = useState<string[]>([]);

  const [posterValuationResult, setPosterValuationResult] =
    useState<PosterValuationResult | null>(null);
  const [posterValuationError, setPosterValuationError] = useState('');
  const [posterValuationLoading, setPosterValuationLoading] = useState(false);
  const [posterValuationLocation, setPosterValuationLocation] =
    useState('Worldwide');

  // State for expanded images
  const [expandedImages, setExpandedImages] = useState<Set<string>>(new Set());

  // Camera state for poster recognition
  const posterInputRef = useRef<HTMLInputElement>(null);
  const [showPosterCamera, setShowPosterCamera] = useState(false);
  const [posterCameraStream, setPosterCameraStream] =
    useState<MediaStream | null>(null);
  const posterVideoRef = useRef<HTMLVideoElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [posterCapturedBlob, setPosterCapturedBlob] = useState<Blob | null>(
    null,
  );
  const [posterVideoDevices, setPosterVideoDevices] = useState<
    MediaDeviceInfo[]
  >([]);
  const [selectedPosterCamera, setSelectedPosterCamera] = useState<string>('');
  const [isEnumeratingPosterDevices, setIsEnumeratingPosterDevices] =
    useState(false);

  // Inventory management types
  interface Batch {
    _id: string;
    num: number;
    itemType: 'vinyl' | 'poster';
    createdAt: string;
    closedAt?: string | null;
    itemSeq: number;
  }

  interface Item {
    _id: string;
    batch: string;
    itemType: 'vinyl' | 'poster';
    sku: string;
    valuations: unknown[];
    images: string[];
    createdAt: string;
    itemNum: number;
    count?: number;
  }

  // Inventory state for Poster view
  const [addToInventory, setAddToInventory] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<Batch | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [savedItem, setSavedItem] = useState<Item | null>(null);
  const [savingItem, setSavingItem] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [inventoryCount, setInventoryCount] = useState<number>(1);

  const handlePosterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPosterError('');
    setPosterLoading(true);
    setPosterResult(null);

    if (posterImages.length === 0) {
      setPosterError('Please select at least one poster image.');
      setPosterLoading(false);
      return;
    }

    try {
      const imagePromises = posterImages.map(async (file) => {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            const base64Data = base64.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = () => reject(reader.error);
        });
        reader.readAsDataURL(file);
        const base64Data = await base64Promise;
        return {
          imageData: base64Data,
          mimeType: file.type,
        };
      });

      const imageDataArray = await Promise.all(imagePromises);
      const firstImage = imageDataArray[0];
      const response = await fetch(`/api/recognize-poster?db=${dbMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify(firstImage),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (!response.ok) {
        setPosterError('Could not identify the poster.');
        setPosterLoading(false);
        return;
      }

      const data = await response.json();
      if (!data.title) {
        setPosterError('Could not identify the poster.');
      } else {
        console.log('Poster recognition result:', data);
        console.log('Inventory matches:', data.inventoryMatches);
        setPosterResult(data);
      }
    } catch {
      setPosterError('An error occurred. Please try again.');
    } finally {
      setPosterLoading(false);
    }
  };

  const handlePosterImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      posterImagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
      setPosterImages((prev) => [...prev, ...files]);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setPosterImagePreviews((prev) => [
            ...prev,
            (ev.target?.result as string) || '',
          ]);
        };
        reader.readAsDataURL(file);
      });
    }
    e.target.value = '';
  };

  const removePosterImage = (index: number) => {
    setPosterImages((prev) => prev.filter((_, i) => i !== index));
    setPosterImagePreviews((prev) => {
      const newPreviews = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });
  };

  // Camera functions for poster recognition
  const getPosterVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === 'videoinput',
      );
      setPosterVideoDevices(videoInputs);
      if (videoInputs.length > 0 && !selectedPosterCamera) {
        setSelectedPosterCamera(videoInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting video devices:', error);
    } finally {
      setIsEnumeratingPosterDevices(false);
    }
  };

  const startPosterVideoStream = async (deviceId: string) => {
    try {
      if (posterCameraStream) {
        posterCameraStream.getTracks().forEach((track) => track.stop());
      }
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: deviceId ? { exact: deviceId } : undefined },
        });
      } catch (err) {
        console.warn(
          'Failed to get selected camera, falling back to default',
          err,
        );
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      setPosterCameraStream(stream);
    } catch (error) {
      console.error('Error starting video stream:', error);
    }
  };

  const handlePosterCameraChange = (deviceId: string) => {
    setSelectedPosterCamera(deviceId);
    if (showPosterCamera && deviceId) {
      startPosterVideoStream(deviceId);
    }
  };

  const handlePosterCapturePhoto = () => {
    if (!posterVideoRef.current) return;
    const video = posterVideoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          setPosterCapturedBlob(blob);
          if (posterImagePreviews.length > 0) {
            posterImagePreviews.forEach((preview) =>
              URL.revokeObjectURL(preview),
            );
          }
          const newPreview = URL.createObjectURL(blob);
          setPosterImagePreviews([newPreview]);
          setPosterImages([
            new File([blob], 'captured.jpg', { type: 'image/jpeg' }),
          ]);
          setShowPosterCamera(false);
          if (posterInputRef.current) {
            posterInputRef.current.value = '';
          }
        }
      }, 'image/jpeg');
    }
  };

  // Inventory functions for Poster view
  const fetchCurrentBatch = useCallback(async () => {
    setBatchLoading(true);
    setBatchError('');
    try {
      const response = await fetch(`/api/batch/current/poster?db=${dbMode}`, {
        headers: {
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const batch = await response.json();
        setCurrentBatch(batch);
      } else {
        setCurrentBatch(null);
      }
    } catch (error) {
      setBatchError('Failed to fetch current batch');

      console.error('Error fetching current batch:', error);
    } finally {
      setBatchLoading(false);
    }
  }, [dbMode, credentials.username, credentials.password, onAuthError]);

  const createNewBatch = async () => {
    setBatchLoading(true);
    setBatchError('');
    try {
      if (currentBatch) {
        await fetch(`/api/batch/${currentBatch.num}?db=${dbMode}`, {
          method: 'PATCH',
          headers: {
            Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          },
        });
      }

      const response = await fetch(`/api/batch?db=${dbMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({ itemType: 'poster' }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const newBatch = await response.json();
        if (
          newBatch &&
          typeof newBatch.num === 'number' &&
          !isNaN(newBatch.num)
        ) {
          setCurrentBatch(newBatch);
        } else {
          setBatchError('Batch creation failed: invalid response from server.');
          setCurrentBatch(null);
        }
      } else {
        const errorData = await response.json();
        setBatchError(errorData.error || 'Failed to create new batch');
        setCurrentBatch(null);
      }
    } catch (error) {
      setBatchError('Failed to create new batch');
      setCurrentBatch(null);

      console.error('Error creating new batch:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const closeCurrentBatch = async () => {
    if (!currentBatch) return;
    setBatchLoading(true);
    setBatchError('');
    try {
      const response = await fetch(
        `/api/batch/${currentBatch.num}?db=${dbMode}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          },
        },
      );

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        setCurrentBatch(null);
      } else {
        const errorData = await response.json();
        setBatchError(errorData.error || 'Failed to close batch');
      }
    } catch (error) {
      setBatchError('Failed to close batch');

      console.error('Error closing batch:', error);
    } finally {
      setBatchLoading(false);
    }
  };

  const saveItemToInventory = async () => {
    if (!currentBatch || !posterResult) return;
    setSavingItem(true);
    setSaveError('');
    try {
      const images: string[] = [...posterImagePreviews];
      const valuations = posterValuationResult ? [posterValuationResult] : [];

      const count =
        Number.isFinite(inventoryCount) && inventoryCount > 0
          ? inventoryCount
          : 1;
      const response = await fetch(`/api/items?db=${dbMode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({
          batchId: currentBatch._id,
          itemType: 'poster',
          valuations,
          images,
          count,
          title: posterResult.title,
          artist: posterResult.artist,
        }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        const item = (await response.json()) as Item;
        setSavedItem(item);
      } else {
        const errorData = await response.json();
        setSaveError(errorData.error || 'Failed to save item');
      }
    } catch (error) {
      setSaveError('Failed to save item');

      console.error('Error saving item:', error);
    } finally {
      setSavingItem(false);
    }
  };

  const resetForm = () => {
    setPosterResult(null);
    setPosterValuationResult(null);
    setPosterImages([]);
    setPosterImagePreviews([]);
    setSavedItem(null);
    setSaveError('');
    setPosterError('');
    setPosterValuationError('');
    setInventoryCount(1);

    // Clean up camera state
    setShowPosterCamera(false);
    setPosterCapturedBlob(null);
    if (posterCameraStream) {
      posterCameraStream.getTracks().forEach((track) => track.stop());
      setPosterCameraStream(null);
    }
  };

  useEffect(() => {
    if (addToInventory) {
      fetchCurrentBatch();
    } else {
      setCurrentBatch(null);
      setSavedItem(null);
    }
  }, [addToInventory, fetchCurrentBatch]);

  useEffect(() => {
    if (addToInventory) {
      fetchCurrentBatch();
    }
  }, [dbMode, addToInventory, fetchCurrentBatch]);

  // Camera effects for poster recognition
  useEffect(() => {
    if (showPosterCamera) {
      (async () => {
        try {
          await navigator.mediaDevices.getUserMedia({ video: true });
        } catch (err) {
          console.warn('Camera permission denied or error:', err);
        }
        getPosterVideoDevices();
      })();
    }
  }, [showPosterCamera]);

  useEffect(() => {
    if (showPosterCamera && posterCameraStream && posterVideoRef.current) {
      posterVideoRef.current.srcObject = posterCameraStream;
      posterVideoRef.current.onloadedmetadata = () => {
        posterVideoRef.current?.play();
      };
    }
  }, [posterCameraStream, showPosterCamera]);

  useEffect(() => {
    if (showPosterCamera && selectedPosterCamera) {
      startPosterVideoStream(selectedPosterCamera);
    }
  }, [showPosterCamera, selectedPosterCamera]);

  useEffect(() => {
    if (!showPosterCamera) return;
    const handleDeviceChange = () => {
      getPosterVideoDevices();
    };
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
    };
  }, [showPosterCamera]);

  return (
    <>
      {/* Inventory Section - mirror of Album, adapted for Poster */}
      <Card className='mb-10 mt-6 w-full'>
        <CardHeader>
          <CardTitle>Inventory Management</CardTitle>
          <CardDescription>
            Add this poster to your inventory for tracking and valuation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='grid w-full items-center gap-4'>
            {/* Add to Inventory Checkbox */}
            <div className='flex items-center space-x-2'>
              <input
                type='checkbox'
                id='add-to-inventory-poster'
                checked={addToInventory}
                onChange={(e) => setAddToInventory(e.target.checked)}
                className='h-4 w-4 rounded border-gray-300'
              />
              <Label htmlFor='add-to-inventory-poster'>Add to Inventory</Label>
            </div>

            {/* Inventory Fields - Only show when checkbox is checked */}
            {addToInventory && (
              <>
                {/* SKU Field */}
                <div className='flex flex-col space-y-1.5'>
                  <Label htmlFor='poster-sku'>SKU</Label>
                  <Input
                    id='poster-sku'
                    value={posterResult && savedItem?.sku ? savedItem.sku : ''}
                    readOnly
                    placeholder='Will be generated when saved'
                  />
                </div>

                {/* Batch Field */}
                <div className='flex flex-col space-y-1.5'>
                  <Label htmlFor='poster-batch'>Batch</Label>
                  <div className='flex space-x-2'>
                    <Input
                      id='poster-batch'
                      value={
                        currentBatch &&
                        typeof currentBatch.num === 'number' &&
                        !isNaN(currentBatch.num)
                          ? `Batch ${currentBatch.num}`
                          : 'None'
                      }
                      readOnly
                      className='flex-1'
                    />
                    <Button
                      type='button'
                      variant='outline'
                      onClick={createNewBatch}
                      disabled={batchLoading}
                    >
                      {batchLoading ? 'Creating...' : 'New'}
                    </Button>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={closeCurrentBatch}
                      disabled={!currentBatch || batchLoading}
                    >
                      {batchLoading ? 'Closing...' : 'Close'}
                    </Button>
                  </div>
                </div>

                {/* Count Field */}
                <div className='flex flex-col space-y-1.5'>
                  <Label htmlFor='poster-count'>Count</Label>
                  <Input
                    id='poster-count'
                    type='number'
                    min={1}
                    value={inventoryCount}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      setInventoryCount(
                        Number.isFinite(value) && value > 0 ? value : 1,
                      );
                    }}
                    className='w-24'
                  />
                </div>

                {/* Action Buttons */}
                <div className='flex space-x-2'>
                  <Button
                    type='button'
                    onClick={saveItemToInventory}
                    disabled={
                      !currentBatch ||
                      !!currentBatch?.closedAt ||
                      savingItem ||
                      !posterResult
                    }
                    className='flex-1'
                  >
                    {savingItem ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={resetForm}
                    className='flex-1'
                  >
                    Next Item
                  </Button>
                </div>

                {/* Error Messages */}
                {batchError && (
                  <p className='text-sm text-destructive'>{batchError}</p>
                )}
                {saveError && (
                  <p className='text-sm text-destructive'>{saveError}</p>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <h2 className='mb-2 text-xl font-bold'>
        Poster Recognition and Valuation
      </h2>
      <p className='mb-4 text-muted-foreground'>
        Get a fair market valuation for your poster. Please provide a separate
        image of each sheet for the poster.
      </p>
      <Card className='w-full'>
        <CardHeader>
          <CardTitle>Poster Recognition</CardTitle>
          <CardDescription>
            Upload or capture poster images to identify and value the poster.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handlePosterSubmit}>
          <CardContent>
            <div className='grid w-full items-center gap-4'>
              <div className='flex flex-col space-y-1.5'>
                <Label htmlFor='poster-image'>Poster Images</Label>
                <div className='flex gap-2'>
                  <Input
                    ref={posterInputRef}
                    id='poster-image'
                    type='file'
                    accept='image/*'
                    multiple
                    onChange={handlePosterImageChange}
                    className='flex-1'
                  />
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() =>
                      document.getElementById('poster-image')?.click()
                    }
                  >
                    Add More
                  </Button>
                </div>
                {!isMobileDevice() && (
                  <Button
                    type='button'
                    variant='outline'
                    className='mt-2'
                    onClick={() => setShowPosterCamera((v) => !v)}
                  >
                    {showPosterCamera ? 'Close Camera' : 'Take Photo'}
                  </Button>
                )}
                {showPosterCamera && (
                  <div className='mt-4 flex flex-col items-center'>
                    <div className='mb-2 w-full max-w-xs'>
                      <Select
                        value={selectedPosterCamera}
                        onValueChange={handlePosterCameraChange}
                        disabled={
                          isEnumeratingPosterDevices ||
                          posterVideoDevices.length === 0
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isEnumeratingPosterDevices
                                ? 'Searching for available cameras...'
                                : posterVideoDevices.length === 0
                                  ? 'No camera devices found'
                                  : 'Select camera'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {isEnumeratingPosterDevices ? (
                            <SelectItem value='__searching__' disabled>
                              Searching for available cameras...
                            </SelectItem>
                          ) : posterVideoDevices.length === 0 ? (
                            <SelectItem value='__none__' disabled>
                              No camera devices found
                            </SelectItem>
                          ) : (
                            posterVideoDevices.map((device) => (
                              <SelectItem
                                key={device.deviceId}
                                value={device.deviceId}
                              >
                                {device.label ||
                                  `Camera ${posterVideoDevices.indexOf(device) + 1}`}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <video
                      ref={posterVideoRef}
                      autoPlay
                      playsInline
                      width={320}
                      height={240}
                      className='mb-2 aspect-video w-full max-w-xs rounded bg-black object-contain'
                      style={{ maxHeight: 320 }}
                    />
                    <Button
                      type='button'
                      onClick={handlePosterCapturePhoto}
                      className='mb-2'
                      disabled={
                        isEnumeratingPosterDevices ||
                        posterVideoDevices.length === 0
                      }
                    >
                      Capture
                    </Button>
                  </div>
                )}
              </div>

              {posterImagePreviews.length > 0 && (
                <div className='space-y-2'>
                  <Label>Uploaded Images ({posterImagePreviews.length})</Label>
                  <div className='grid gap-2 md:grid-cols-2 lg:grid-cols-3'>
                    {posterImagePreviews.map((preview, index) => (
                      <div key={index} className='relative'>
                        <img
                          src={preview}
                          alt={`Poster preview ${index + 1}`}
                          className='h-32 w-full rounded border object-cover shadow'
                          style={{ background: '#f3f3f3' }}
                          onError={(e) => {
                            (
                              e.currentTarget as HTMLImageElement
                            ).style.display = 'none';
                          }}
                        />
                        <Button
                          type='button'
                          variant='destructive'
                          size='sm'
                          className='absolute -right-2 -top-2 h-6 w-6 rounded-full p-0'
                          onClick={() => removePosterImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {posterError && (
                <p className='text-sm text-destructive'>{posterError}</p>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button className='w-full' type='submit' disabled={posterLoading}>
              {posterLoading ? 'Identifying...' : 'Identify Poster'}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {posterResult && (
        <Card className='mt-6 w-full'>
          <CardHeader>
            <CardTitle>Poster Identification Result</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='grid gap-4 md:grid-cols-2'>
              <div>
                <h3 className='font-semibold'>Title</h3>
                <p className='text-muted-foreground'>{posterResult.title}</p>
              </div>
              {posterResult.artist && (
                <div>
                  <h3 className='font-semibold'>Artist</h3>
                  <p className='text-muted-foreground'>{posterResult.artist}</p>
                </div>
              )}
              <div>
                <h3 className='font-semibold'>Year</h3>
                <p className='text-muted-foreground'>{posterResult.year}</p>
              </div>
              <div>
                <h3 className='font-semibold'>Type</h3>
                <p className='text-muted-foreground'>{posterResult.type}</p>
              </div>
              <div>
                <h3 className='font-semibold'>Dimensions</h3>
                <p className='text-muted-foreground'>
                  {posterResult.dimensions}
                </p>
              </div>
              <div>
                <h3 className='font-semibold'>Condition</h3>
                <p className='text-muted-foreground'>
                  {posterResult.condition}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Inventory Matches */}
      {posterResult?.inventoryMatches &&
        posterResult.inventoryMatches.length > 0 && (
          <Card className='mt-6 w-full'>
            <CardHeader>
              <CardTitle>Existing Inventory</CardTitle>
              <CardDescription>
                The poster is a potential match for the following items in
                existing inventory:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                {posterResult.inventoryMatches.map((item) => (
                  <div key={item._id} className='rounded-lg border p-4'>
                    <div className='mb-3 flex items-start justify-between'>
                      <div>
                        <h4 className='font-semibold'>SKU: {item.sku}</h4>
                        {item.title && (
                          <p className='text-sm text-muted-foreground'>
                            Title: {item.title}
                          </p>
                        )}
                        {item.artist && (
                          <p className='text-sm text-muted-foreground'>
                            Artist: {item.artist}
                          </p>
                        )}
                        <p className='text-sm text-muted-foreground'>
                          Added: {new Date(item.createdAt).toLocaleDateString()}
                        </p>
                        {item.count && item.count > 1 && (
                          <p className='text-sm text-muted-foreground'>
                            Count: {item.count}
                          </p>
                        )}
                      </div>
                    </div>

                    {item.images && item.images.length > 0 && (
                      <div className='space-y-2'>
                        <h5 className='text-sm font-medium'>Images:</h5>
                        <div className='grid gap-2 md:grid-cols-3 lg:grid-cols-4'>
                          {item.images.map((image, imageIndex) => {
                            const imageKey = `${item._id}-${imageIndex}`;
                            const isExpanded = expandedImages.has(imageKey);

                            return (
                              <div key={imageIndex} className='relative'>
                                {isExpanded ? (
                                  <div className='relative'>
                                    <img
                                      src={image}
                                      alt={`Item image ${imageIndex + 1}`}
                                      className='w-full rounded border shadow-lg'
                                      style={{ background: '#f3f3f3' }}
                                      onError={(e) => {
                                        (
                                          e.currentTarget as HTMLImageElement
                                        ).style.display = 'none';
                                      }}
                                    />
                                    <Button
                                      type='button'
                                      variant='destructive'
                                      size='sm'
                                      className='absolute right-2 top-2 h-6 w-6 rounded-full p-0'
                                      onClick={() => {
                                        const newExpanded = new Set(
                                          expandedImages,
                                        );
                                        newExpanded.delete(imageKey);
                                        setExpandedImages(newExpanded);
                                      }}
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ) : (
                                  <img
                                    src={image}
                                    alt={`Item image ${imageIndex + 1}`}
                                    className='h-24 w-full cursor-pointer rounded border object-cover shadow transition-opacity hover:opacity-80'
                                    style={{ background: '#f3f3f3' }}
                                    onClick={() => {
                                      const newExpanded = new Set(
                                        expandedImages,
                                      );
                                      newExpanded.add(imageKey);
                                      setExpandedImages(newExpanded);
                                    }}
                                    onError={(e) => {
                                      (
                                        e.currentTarget as HTMLImageElement
                                      ).style.display = 'none';
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      <div className='mt-10'>
        <h2 className='mb-2 text-xl font-bold'>Poster Valuation</h2>
        <p className='mb-4 text-muted-foreground'>
          Get a fair market valuation for your poster. Please provide a clear
          image of the poster.
        </p>
        <Card className='w-full'>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setPosterValuationError('');
              setPosterValuationLoading(true);
              setPosterValuationResult(null);

              if (posterImages.length === 0) {
                setPosterValuationError(
                  'Please select at least one poster image for valuation.',
                );
                setPosterValuationLoading(false);
                return;
              }

              const imagePromises = posterImages.map(async (file) => {
                const reader = new FileReader();
                const base64Promise = new Promise<string>((resolve, reject) => {
                  reader.onload = () => {
                    const base64 = reader.result as string;
                    const base64Data = base64.split(',')[1];
                    resolve(base64Data);
                  };
                  reader.onerror = () => reject(reader.error);
                });
                reader.readAsDataURL(file);
                const base64Data = await base64Promise;
                return {
                  imageData: base64Data,
                  mimeType: file.type,
                };
              });

              const imageDataArray = await Promise.all(imagePromises);
              const firstImage = imageDataArray[0];

              try {
                const response = await fetch('/api/poster-valuation', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
                  },
                  body: JSON.stringify({
                    imageData: firstImage.imageData,
                    mimeType: firstImage.mimeType,
                    location: posterValuationLocation,
                    title: posterResult?.title,
                    artist: posterResult?.artist,
                    type: posterResult?.type,
                  }),
                });
                if (response.status === 401) {
                  if (onAuthError) onAuthError();
                  return;
                }
                if (!response.ok) {
                  const errorData = await response.json();
                  setPosterValuationError(
                    errorData.error || 'Failed to get poster valuation',
                  );
                  setPosterValuationLoading(false);
                  return;
                }
                const data = await response.json();
                setPosterValuationResult(data);
              } catch {
                setPosterValuationError('An error occurred. Please try again.');
              } finally {
                setPosterValuationLoading(false);
              }
            }}
          >
            <CardContent>
              <div className='grid w-full items-center gap-4'>
                <div className='mt-4 flex flex-col space-y-1.5'>
                  <LocationLookup
                    onLocationFound={setPosterValuationLocation}
                    defaultLocation='Worldwide'
                  />
                </div>
                {posterValuationError && (
                  <p className='text-sm text-destructive'>
                    {posterValuationError}
                  </p>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                className='w-full'
                type='submit'
                disabled={posterValuationLoading}
              >
                {posterValuationLoading
                  ? 'Getting Valuation...'
                  : 'Get Valuation'}
              </Button>
            </CardFooter>
          </form>
        </Card>

        {posterValuationResult && (
          <Card className='mt-6 w-full'>
            <CardHeader>
              <CardTitle>Poster Valuation Result</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Price Range</h3>
                <p className='text-2xl font-bold text-primary'>
                  {posterValuationResult.priceRange.currency}{' '}
                  {posterValuationResult.priceRange.min.toLocaleString()} -{' '}
                  {posterValuationResult.priceRange.currency}{' '}
                  {posterValuationResult.priceRange.max.toLocaleString()}
                </p>
              </div>

              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Justification</h3>
                <p className='text-muted-foreground'>
                  {posterValuationResult.justification}
                </p>
              </div>

              {posterValuationResult.marketData.recent?.listings?.length >
                0 && (
                <div className='mb-6'>
                  <h3 className='mb-2 text-lg font-semibold'>Recent Sales</h3>
                  <ul className='space-y-2'>
                    {posterValuationResult.marketData.recent.listings.map(
                      (sale, index) => (
                        <li key={index} className='border-b pb-2'>
                          <div className='flex items-start justify-between'>
                            <div>
                              <p className='font-medium'>
                                {sale.currency} {sale.price.toLocaleString()}
                              </p>
                              <p className='text-sm text-muted-foreground'>
                                {sale.location} • {sale.date}
                                {sale.condition && (
                                  <span>
                                    {' '}
                                    •{' '}
                                    <span className='font-semibold text-primary'>
                                      {sale.condition}
                                    </span>
                                  </span>
                                )}
                              </p>
                            </div>
                            {sale.url && (
                              <a
                                href={sale.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-sm text-blue-500 hover:underline'
                              >
                                View Sale
                              </a>
                            )}
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                  {posterValuationResult.marketData.recent.explanation && (
                    <p className='mt-2 text-sm text-muted-foreground'>
                      {posterValuationResult.marketData.recent.explanation}
                    </p>
                  )}
                </div>
              )}

              {posterValuationResult.marketData.current?.listings?.length >
                0 && (
                <div className='mb-6'>
                  <h3 className='mb-2 text-lg font-semibold'>
                    Current Listings
                  </h3>
                  <ul className='space-y-2'>
                    {posterValuationResult.marketData.current.listings.map(
                      (listing, index) => (
                        <li key={index} className='border-b pb-2'>
                          <div className='flex items-start justify-between'>
                            <div>
                              <p className='font-medium'>
                                {listing.currency}{' '}
                                {listing.price.toLocaleString()}
                              </p>
                              <p className='text-sm text-muted-foreground'>
                                {listing.location}
                                {listing.condition && (
                                  <span>
                                    {' '}
                                    •{' '}
                                    <span className='font-semibold text-primary'>
                                      {listing.condition}
                                    </span>
                                  </span>
                                )}
                              </p>
                            </div>
                            {listing.url && (
                              <a
                                href={listing.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-sm text-blue-500 hover:underline'
                              >
                                View Listing
                              </a>
                            )}
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                  {posterValuationResult.marketData.current.explanation && (
                    <p className='mt-2 text-sm text-muted-foreground'>
                      {posterValuationResult.marketData.current.explanation}
                    </p>
                  )}
                </div>
              )}

              {posterValuationResult.citations &&
                posterValuationResult.citations.length > 0 && (
                  <div className='mt-6'>
                    <h3 className='mb-2 text-lg font-semibold'>Citations</h3>
                    <ul className='list-disc space-y-1 pl-4'>
                      {posterValuationResult.citations.map((url, index) => (
                        <li key={index}>
                          <a
                            href={url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-500 hover:underline'
                          >
                            {url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {posterValuationResult.searchResults &&
                posterValuationResult.searchResults.length > 0 && (
                  <div className='mt-6'>
                    <h3 className='mb-2 text-lg font-semibold'>
                      Search Results
                    </h3>
                    <ul className='space-y-2'>
                      {posterValuationResult.searchResults.map(
                        (result, index) => (
                          <li key={index} className='border-b pb-2'>
                            <a
                              href={result.url}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-blue-500 hover:underline'
                            >
                              {result.title}
                            </a>
                            {result.date && (
                              <span className='ml-2 text-sm text-muted-foreground'>
                                ({result.date})
                              </span>
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

export default Poster;
